import { Box } from '@mui/material';
import { FC, MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';

import {
  MAX_PANEL_WIDTH, MIN_PANEL_WIDTH, RESIZE_HANDLE_WIDTH, clampPanelWidth,
} from './panelWidth';

type Props = {
  /** どちら側のパネルを動かすか。left は右へ引くほど広がる */
  side: 'left' | 'right';
  /** 現在の幅。ドラッグ開始時の基準にする */
  width: number;
  /** ダブルクリックで戻す幅 */
  defaultWidth: number;
  /** 読み上げと操作の識別に使う名前 */
  label: string;
  /** clamp 済みの幅を渡す。ドラッグ中は移動のたびに呼ぶ */
  onWidthChange: (width: number) => void;
  /** 掴んでいる間だけ true。幅の transition を止めるのに使う */
  onResizingChange?: (resizing: boolean) => void;
  /**
   * このパネル以外に確保しておく幅（反対側のパネル＋ハンドル＋地図の最小幅）。
   * 渡すと、親要素の幅から逆算した上限でドラッグを止める（panelWidth.reservedWidthFor）。
   */
  reservedWidth?: number;
};

/**
 * 左右リストの境界に置くリサイズハンドル。
 * サンプル (route-planner: pages/MapPage.tsx startLeftResize / startRightResize) と同じく
 * ドラッグで幅を変え、ダブルクリックで既定幅に戻す。
 *
 * Why window に張る: ハンドルは 6px しかないので、少し速く動かすとポインタが要素の外へ出る。
 * 要素の mousemove だけを見ていると追従が途切れる。
 */
const PanelResizeHandle: FC<Props> = ({
  side, width, defaultWidth, label, onWidthChange, onResizingChange, reservedWidth,
}) => {
  const [resizing, setResizing] = useState(false);
  /** ドラッグ中の後片付け。mouseup とアンマウントの両方から同じ処理を呼ぶ */
  const detachRef = useRef<(() => void) | null>(null);

  useEffect(() => () => detachRef.current?.(), []);

  const onMouseDown = useCallback((event: ReactMouseEvent) => {
    // 押した時点でテキスト選択やドラッグ開始が始まると、掴んだ瞬間に選択が走る
    event.preventDefault();
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING_MAP, button_name: `${label}（ドラッグ）` });

    const startX = event.clientX;
    const startWidth = width;

    // 上限は「親（3 ペインの器）の幅 − 確保しておく幅」でも抑える。
    // jsdom など幅が測れない場所では 0 が返るので、その場合は既定の上限だけを使う
    const containerWidth = event.currentTarget.parentElement?.getBoundingClientRect().width ?? 0;
    const maxWidth = reservedWidth != null && containerWidth > 0
      ? Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, containerWidth - reservedWidth))
      : MAX_PANEL_WIDTH;

    const onMove = (moveEvent: globalThis.MouseEvent) => {
      const delta = side === 'left' ? moveEvent.clientX - startX : startX - moveEvent.clientX;
      onWidthChange(Math.min(maxWidth, clampPanelWidth(startWidth + delta)));
    };
    const detach = () => {
      detachRef.current = null;
      setResizing(false);
      onResizingChange?.(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', detach);
      window.removeEventListener('blur', detach);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    detachRef.current = detach;
    setResizing(true);
    onResizingChange?.(true);
    // 掴んだまま地図やリストの上を通っても、カーソルと選択状態が変わらないようにする
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', detach);
    // ドラッグ中に Cmd+Tab などでフォーカスを失うと mouseup を取り逃す。追従が残ると
    // 戻ってきて動かしただけで幅が変わり続け、body の文字選択も止まったままになる
    window.addEventListener('blur', detach);
  }, [label, onResizingChange, onWidthChange, reservedWidth, side, width]);

  const onDoubleClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING_MAP, button_name: `${label}（既定に戻す）` });
    onWidthChange(defaultWidth);
  }, [defaultWidth, label, onWidthChange]);

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      sx={{
        // 細すぎると掴めず、太いと境界線に見えない
        width: RESIZE_HANDLE_WIDTH,
        flexShrink: 0,
        cursor: 'col-resize',
        // 掴める場所だと分かるのは hover / ドラッグ中だけ。常時色を置くと境界線が二重に見える
        bgcolor: resizing ? 'hsl(var(--primary) / 0.35)' : 'transparent',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'hsl(var(--primary) / 0.25)' },
      }}
    />
  );
};

export default PanelResizeHandle;
